// src/app/components/atoms/Chip/ChipAtom.tsx

import React, { forwardRef } from 'react';
import { useTranslation } from 'localization';
import { Chip, ChipProps, ChipRemoveEvent } from 'primereact/chip';
import GlyphAtom from '../Glyph/GlyphAtom';
import './ChipAtom.scss';

export interface ChipAtomProps extends Omit<ChipProps, 'onRemove'> {
  /** Text label to display inside the chip */
  label: string;
  /** Optional icon: PrimeIcon CSS class (e.g. "pi pi-check") or Iconify name (e.g. "lucide:check") */
  icon?: string;
  /** Optional image URL to render as an avatar */
  image?: string;
  /** Custom template for the chip's content */
  template?: React.ReactNode | ((props: ChipProps) => React.ReactNode);
  /** Whether the remove icon should be shown */
  removable?: boolean;
  /** Override the default remove icon */
  removeIcon?: React.ReactNode;
  /**
   * Fires when the chip's remove icon is clicked,
   * receiving the chip's label. Return `false` to cancel removal.
   */
  onRemove?: (label: string) => boolean | void;
  /**
   * Renders a lock affordance alongside the chip's existing icon slot and
   * forces the chip non-dismissable, regardless of `removable`/`onRemove`.
   * An origin STATE (e.g. "this clause is context-authored"), not a display
   * toggle — see `docs/design/context-view-column-filters.md` §4.1.
   */
  locked?: boolean;
  /**
   * Accessible reason shown as the lock affordance's title/aria-label when
   * `locked` — e.g. "Defined in this context". Required so the lock state is
   * never colour-only and is exposed to assistive tech (EAINT-781).
   */
  lockedReason?: string;
  /**
   * Renders a "no longer available" warning affordance alongside the chip's
   * existing icon slot. A STATE, not a different kind of chip — the same
   * argument that made `locked` a prop rather than a sibling component
   * (EAINT-789 § Design System Reuse Audit). Unlike `locked`, does NOT force
   * `removable={false}`: a degraded clause is stale and safe to drop, so
   * whether it can be removed is still decided by the caller's ownership
   * check, orthogonal to this flag.
   */
  unavailable?: boolean;
  /**
   * Accessible reason shown as the warning affordance's title/aria-label when
   * `unavailable` — e.g. "No longer available". Required for the same reason
   * as `lockedReason`: the state must never be colour-only.
   */
  unavailableReason?: string;
  /**
   * Accessible name for the remove control. Defaults to "Remove {label}" —
   * override only when the label alone doesn't identify what is removed.
   */
  removeAriaLabel?: string;
  /**
   * Provenance tone for the Stream/Trajectory chip vocabulary (see the
   * spec's Design System Reuse Audit — `StatusBadgeAtom.tone` covers status
   * pills; this covers the calc/claim/warn provenance chips specifically).
   * Undefined renders the plain, untoned chip unchanged from today.
   */
  tone?: 'neutral' | 'calc' | 'claim' | 'warn';
  /**
   * Dashed border, independent of `tone` — the "needs validation" chip is
   * `tone="warn" dashed`, but `dashed` doesn't imply a tone nor vice versa.
   * `locked`'s dashed treatment (above) is the one this generalizes.
   */
  dashed?: boolean;
  /**
   * Parity instrumentation for the remove control specifically — PrimeReact's
   * default (non-templated) remove affordance is an `<i>` icon, not a real
   * `<button>`, so a caller that needs the remove control individually
   * addressable (e.g. a per-item id in a repeated list) supplies this to force
   * the custom template's real `<button>` to render, carrying `data-datum`.
   */
  removeDatum?: string;
  /** Parity instrumentation — `data-kind` on the remove control, beside `removeDatum`. Defaults to `button`. */
  removeDatumKind?: string;
}

const ChipAtom = forwardRef<Chip, ChipAtomProps>((props, ref) => {
  const {
    label,
    icon,
    image,
    template,
    removable = false,
    removeIcon,
    onRemove,
    locked = false,
    lockedReason,
    unavailable = false,
    unavailableReason,
    removeAriaLabel,
    tone,
    dashed = false,
    removeDatum,
    removeDatumKind,
    ...rest
  } = props;

  const { t } = useTranslation();

  // Must return boolean: false to prevent removal, true to allow
  const handleRemove = (e: ChipRemoveEvent): boolean => {
    if (onRemove) {
      const result = onRemove(e.value);
      // if user returns false, we cancel; otherwise proceed
      return result !== false;
    }
    // default allow
    return true;
  };

  // When icon is an Iconify name (set:name), use GlyphAtom via template since
  // PrimeReact's Chip only understands CSS class strings for its icon prop.
  const isIconify = typeof icon === 'string' && icon.includes(':');
  // `locked`/`unavailable` need a custom template too — PrimeReact's built-in
  // `icon` prop has no second slot for the lock/warning decorator, and the
  // icon slot above is already spoken for by the lane glyph.
  const needsCustomTemplate = isIconify || locked || unavailable || !!removeDatum;
  // PrimeReact's Chip only appends its own `.p-chip-remove-icon` inside its
  // internal `createContent()` renderer, which a custom `template` bypasses
  // entirely — the `removable`/`onRemove` props passed to `<Chip>` below are
  // then silently inert. `locked` never notices (it forces removable false),
  // but `unavailable` is documented to stay removable, so the template must
  // render its own remove control and route it through the same
  // `handleRemove` cancel-by-returning-false contract.
  const showsRemoveControl = !locked && removable;
  const resolvedTemplate: React.ReactNode | ((props: ChipProps) => React.ReactNode) | undefined =
    template ?? (needsCustomTemplate
      ? (
        <>
          {icon && (isIconify
            ? <GlyphAtom glyph={icon} className="p-chip-icon" />
            : <i className={`p-chip-icon ${icon}`} />)}
          <span className="p-chip-text">{label}</span>
          {/* `role="img"` is load-bearing, not decoration: ARIA does not name
              generic elements, so `aria-label` on a role-less <span> is
              dropped by most screen readers and the state would be conveyed
              by colour alone. */}
          {locked && (
            <span className="chip-atom__lock" role="img" title={lockedReason} aria-label={lockedReason ?? label}>
              <i className="pi pi-lock" aria-hidden="true" />
            </span>
          )}
          {unavailable && (
            <span
              className="chip-atom__unavailable"
              role="img"
              title={unavailableReason}
              aria-label={unavailableReason ?? label}
            >
              <i className="pi pi-exclamation-triangle" aria-hidden="true" />
            </span>
          )}
          {showsRemoveControl && (
            // A real <button>, not `<i role="button">`: it carries an
            // accessible name, is keyboard-reachable, and answers Enter AND
            // Space natively — the hand-rolled version answered neither
            // reliably nor announced anything (Sonar S6819 / S1082).
            <button
              type="button"
              className="p-chip-remove-icon pi pi-times-circle"
              aria-label={removeAriaLabel ?? t('common.actions.removeItem', { label })}
              data-datum={removeDatum}
              data-kind={removeDatum ? removeDatumKind ?? 'button' : undefined}
              onClick={(e) => {
                e.stopPropagation();
                handleRemove({ value: label } as ChipRemoveEvent);
              }}
            />
          )}
        </>
      )
      : undefined);

  const wrapperClasses = [
    'prime-chip-wrapper',
    locked && 'prime-chip-wrapper--locked',
    unavailable && 'prime-chip-wrapper--unavailable',
    tone && `chip-atom--tone-${tone}`,
    dashed && 'chip-atom--dashed',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClasses}>
      <Chip
        ref={ref}
        label={resolvedTemplate ? undefined : label}
        icon={isIconify ? undefined : icon}
        image={image}
        template={resolvedTemplate}
        removable={locked ? false : removable}
        removeIcon={removeIcon}
        onRemove={handleRemove}
        {...rest}
      />
    </div>
  );
});

ChipAtom.displayName = 'ChipAtom';

export default ChipAtom;
